import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Calendar,
  Search,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Video,
  User,
  Building2,
  Phone,
  Mail,
  Link,
  FileText,
  Check,
  XCircle,
  Edit2,
  Eye,
  ExternalLink,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com';
const authHeader = () => ({ Authorization: 'Bearer ' + localStorage.getItem('session_token') });

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
};

const STATUS_COLORS = {
  Pending: 'bg-purple-100 text-purple-700',
  Scheduled: 'bg-blue-100 text-blue-700',
  Completed: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
  Rescheduled: 'bg-yellow-100 text-yellow-700',
  Missed: 'bg-gray-100 text-gray-600',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtTime = (t) => t || '—';

const Badge = ({ label, colorClass }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass || 'bg-gray-100 text-gray-600'}`}>
    {label}
  </span>
);

// ── Contact Search Dropdown ───────────────────────────────────────────────────
const ContactSearchDropdown = ({ onSelect, placeholder = 'Search contacts…' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiFetch(`/api/crm/contacts/search?q=${encodeURIComponent(query)}`);
        setResults(data.contacts || data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const pick = (c) => {
    onSelect(c);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 focus:border-[#843FEF]"
        />
        {loading && (
          <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {results.map((c) => (
            <button
              key={c.id || c._id}
              onClick={() => pick(c)}
              className="w-full text-left px-4 py-2.5 hover:bg-purple-50 flex flex-col border-b border-gray-50 last:border-0"
            >
              <span className="text-sm font-medium text-gray-800">{c.contact_person || c.name}</span>
              <span className="text-xs text-gray-500">{c.organization_name || c.organization} · {c.mobile_number || c.mobile}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Schedule Demo Drawer ──────────────────────────────────────────────────────
const ScheduleDemoDrawer = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    contact_id: '',
    organization_name: '',
    contact_person: '',
    mobile_number: '',
    email: '',
    demo_date: '',
    demo_time: '',
    duration_minutes: '30',
    meeting_platform: 'Google Meet',
    meeting_url: '',
    assigned_team_member: '',
    notes: '',
    status: 'Pending',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleContactSelect = (c) => {
    setForm((f) => ({
      ...f,
      contact_id: c.id || c._id,
      contact_person: c.contact_person || c.name || '',
      organization_name: c.organization_name || c.organization || '',
      mobile_number: c.mobile_number || c.mobile || '',
      email: c.email || '',
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await apiFetch('/api/crm/demos', { method: 'POST', body: JSON.stringify(form) });
      onSaved();
    } catch {
      setError('Failed to schedule demo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 focus:border-[#843FEF] bg-white';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      {/* Drawer */}
      <div className="w-96 bg-white shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#843FEF]" />
            Schedule New Demo
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <div>
            <label className={labelClass}>Contact <span className="text-red-500">*</span></label>
            <ContactSearchDropdown onSelect={handleContactSelect} placeholder="Search contact…" />
          </div>

          <div>
            <label className={labelClass}>Organization Name</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                value={form.organization_name}
                onChange={(e) => setField('organization_name', e.target.value)}
                placeholder="Auto-filled from contact"
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Contact Person</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                value={form.contact_person}
                onChange={(e) => setField('contact_person', e.target.value)}
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Mobile Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                value={form.mobile_number}
                onChange={(e) => setField('mobile_number', e.target.value)}
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Demo Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                required
                value={form.demo_date}
                onChange={(e) => setField('demo_date', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Demo Time <span className="text-red-500">*</span></label>
              <input
                type="time"
                required
                value={form.demo_time}
                onChange={(e) => setField('demo_time', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Duration</label>
            <select
              value={form.duration_minutes}
              onChange={(e) => setField('duration_minutes', e.target.value)}
              className={inputClass}
            >
              {[15, 30, 45, 60].map((d) => (
                <option key={d} value={d}>{d} minutes</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Meeting Platform <span className="text-red-500">*</span></label>
            <select
              value={form.meeting_platform}
              onChange={(e) => setField('meeting_platform', e.target.value)}
              className={inputClass}
            >
              {['Google Meet'].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Meeting URL</label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="url"
                value={form.meeting_url}
                onChange={(e) => setField('meeting_url', e.target.value)}
                placeholder="Paste meeting link here…"
                className={`${inputClass} pl-9`}
              />
            </div>
            {form.meeting_platform === 'Google Meet' && (
              <a
                href="https://meet.google.com/new"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-1.5 text-xs text-[#843FEF] font-medium hover:underline"
              >
                <Video className="w-3 h-3" />
                Create a new Google Meet → copy the link and paste above
              </a>
            )}
          </div>

          <div>
            <label className={labelClass}>Assigned Team Member</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                value={form.assigned_team_member}
                onChange={(e) => setField('assigned_team_member', e.target.value)}
                placeholder="Team member name"
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Additional notes…"
                className={`${inputClass} pl-9 resize-none`}
              />
            </div>
          </div>
        </form>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !form.demo_date || !form.demo_time}
            className="flex-1 py-2.5 bg-[#843FEF] text-white rounded-lg text-sm font-semibold hover:bg-[#7230d4] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Scheduling…' : 'Schedule Demo'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Cancel Modal ──────────────────────────────────────────────────────────────
const CancelModal = ({ demo, onClose, onSaved }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/crm/demos/${demo.id || demo._id}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({ reason }),
      });
      onSaved();
    } catch {
      setError('Failed to cancel demo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-800">Cancel Demo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {error && (
          <div className="mb-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        <p className="text-sm text-gray-600 mb-3">
          Cancel demo with <strong>{demo.contact_person || demo.contact?.name}</strong>?
        </p>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for cancellation…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30 resize-none"
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Back
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-60"
          >
            {saving ? 'Cancelling…' : 'Confirm Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reschedule Modal ──────────────────────────────────────────────────────────
const RescheduleModal = ({ demo, onClose, onSaved }) => {
  const [form, setForm] = useState({ demo_date: '', demo_time: '', meeting_url: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/crm/demos/${demo.id || demo._id}/reschedule`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      onSaved();
    } catch {
      setError('Failed to reschedule demo.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-800">Reschedule Demo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        {error && (
          <div className="mb-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Date <span className="text-red-500">*</span></label>
            <input type="date" value={form.demo_date} onChange={(e) => setForm((f) => ({ ...f, demo_date: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Time <span className="text-red-500">*</span></label>
            <input type="time" value={form.demo_time} onChange={(e) => setForm((f) => ({ ...f, demo_time: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Meeting URL</label>
            <input
              type="url"
              value={form.meeting_url}
              onChange={(e) => setForm((f) => ({ ...f, meeting_url: e.target.value }))}
              placeholder="https://…"
              className={inputClass}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Back
          </button>
          <button
            onClick={submit}
            disabled={saving || !form.demo_date || !form.demo_time}
            className="flex-1 py-2 bg-[#843FEF] text-white rounded-lg text-sm font-medium hover:bg-[#7230d4] disabled:opacity-60"
          >
            {saving ? 'Rescheduling…' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Details Modal ─────────────────────────────────────────────────────────────
const ConfirmDemoModal = ({ demo, onClose, onConfirm }) => {
  const [meetingUrl, setMeetingUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateUrl = (url) => {
    if (!url) return 'Meeting URL is required';
    if (!url.startsWith('https://meet.google.com/')) return 'Please provide a valid Google Meet link (starting with https://meet.google.com/)';
    // Check for common placeholders or incomplete links
    if (url === 'https://meet.google.com/' || url.includes('...')) return 'Please paste the actual meeting link from your browser address bar';
    return '';
  };

  const handleConfirm = async () => {
    const err = validateUrl(meetingUrl);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setLoading(true);
    await onConfirm(demo, meetingUrl);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-gray-800">Confirm & Schedule Demo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-purple-50 rounded-xl border border-purple-100 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-5 h-5 text-[#843FEF]" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">{demo.organization_name || demo.organization}</p>
                <p className="text-xs text-gray-500 mt-0.5">{fmtDate(demo.demo_date)} at {fmtTime(demo.demo_time)}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <Link className="w-3.5 h-3.5 text-gray-400" />
              Google Meet Link <span className="text-red-500">*</span>
            </label>
            <div className="relative group">
              <input
                type="url"
                value={meetingUrl}
                onChange={(e) => {
                  setMeetingUrl(e.target.value);
                  if (error) setError('');
                }}
                placeholder="https://meet.google.com/abc-defg-hij"
                className={`w-full border rounded-xl pl-4 pr-4 py-3 text-sm focus:outline-none focus:ring-2 bg-gray-50 transition-all ${
                  error ? 'border-red-300 focus:ring-red-100 focus:border-red-500' : 'border-gray-200 focus:ring-[#843FEF]/20 focus:border-[#843FEF]'
                }`}
              />
            </div>
            {error && <p className="mt-1.5 text-[11px] text-red-500 font-medium pl-1">{error}</p>}
            
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                <strong>How to get a real link:</strong><br/>
                1. Click the button below to open Google Meet.<br/>
                2. Start/Join the meeting.<br/>
                3. <strong>Copy the URL from your browser's address bar</strong> and paste it above.
              </p>
            </div>

            <a 
              href="https://meet.google.com/new" 
              target="_blank" 
              rel="noopener noreferrer"
              className="mt-3 flex items-center gap-2 text-xs font-bold text-white bg-[#843FEF] hover:bg-[#7230d4] transition-all w-full justify-center py-2.5 rounded-lg shadow-sm"
            >
              <Video className="w-4 h-4" />
              1. Create Real Meeting Link
            </a>
          </div>

          <div className="pt-2 border-t border-gray-100 mt-2">
            <button
              disabled={loading}
              onClick={handleConfirm}
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-100 disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              2. Confirm & Send Mail
            </button>
            <button
              disabled={loading}
              onClick={onClose}
              className="mt-3 w-full py-2 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailsModal = ({ demo, onClose, onConfirm }) => {
  const [meetingUrl, setMeetingUrl] = useState(demo.meeting_url || '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-gray-800">Demo Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col gap-3 mb-2">
            {(demo.status === 'Pending' || demo.notes?.includes('[PENDING REQUEST]')) && (
              <button
                onClick={() => onConfirm(demo)}
                className="flex items-center justify-center gap-2 w-full mt-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors shadow-lg shadow-green-200"
              >
                <CheckCircle className="w-4 h-4" />
                Confirm & Send Mail
              </button>
            )}
            {demo.meeting_url ? (
              <a
                href={demo.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#843FEF] text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition-colors shadow-lg shadow-purple-100"
              >
                <Video className="w-4 h-4" />
                Join Google Meet
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
            ) : (demo.status === 'Scheduled' || demo.status === 'Rescheduled') && (
              <div className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-100 text-gray-500 rounded-xl text-sm font-medium italic border border-gray-200">
                <Video className="w-4 h-4 opacity-50" />
                No Meeting Link Provided
              </div>
            )}
            {demo.email && (
              <a
                href={`mailto:${demo.email}`}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-blue-500 text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Send Email
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
            )}
          </div>
          <div className="space-y-3">
            {[
              { label: 'Contact', value: demo.contact_person || demo.contact?.name },
              { label: 'Organization', value: demo.organization_name || demo.organization },
              { label: 'State', value: demo.state },
              { label: 'District', value: demo.district },
              { label: 'Mobile', value: demo.mobile_number || demo.contact?.mobile },
              { label: 'Email', value: demo.email || demo.contact?.email },
              { label: 'Date', value: fmtDate(demo.demo_date) },
              { label: 'Time', value: fmtTime(demo.demo_time) },
              { label: 'Duration', value: demo.duration_minutes ? `${demo.duration_minutes} min` : '—' },
              { label: 'Platform', value: demo.meeting_platform },
              { label: 'Assigned To', value: demo.assigned_team_member },
              { label: 'Notes', value: demo.notes },
            ].map(({ label, value }) => (
              <div key={label} className="flex gap-3 border-b border-gray-50 pb-2">
                <span className="text-xs font-medium text-gray-500 w-28 flex-shrink-0 pt-0.5">{label}</span>
                <span className="text-sm text-gray-800 break-all">{value || '—'}</span>
              </div>
            ))}
            <div className="flex gap-3">
              <span className="text-xs font-medium text-gray-500 w-28 flex-shrink-0 pt-0.5">Status</span>
              <Badge label={demo.status} colorClass={STATUS_COLORS[demo.status]} />
            </div>
          </div>
        </div>
        <button onClick={onClose} className="mt-6 w-full py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
          Close
        </button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CRMScheduleDemo = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', date_from: '', date_to: '' });
  const [showDrawer, setShowDrawer] = useState(false);
  const [modal, setModal] = useState(null); // { type: 'cancel'|'reschedule'|'details', demo }
  const [actionMsg, setActionMsg] = useState('');
  const limit = 20;

  const fetchDemos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filters.status) params.set('status', filters.status);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const data = await apiFetch(`/api/crm/demos?${params.toString()}`);
      setDemos(data.demos || data || []);
      setTotal(data.total || 0);
    } catch {
      setDemos([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchDemos(); }, [fetchDemos]);

  const handleComplete = async (demo) => {
    try {
      await apiFetch(`/api/crm/demos/${demo.id || demo._id}/complete`, {
        method: 'PUT',
      });
      setActionMsg('Demo marked as Completed.');
      fetchDemos();
    } catch {
      setActionMsg('Failed to update demo.');
    }
    setTimeout(() => setActionMsg(''), 3000);
  };

  const handleConfirmAction = async (demo, meetingUrl) => {
    try {
      await apiFetch(`/api/crm/demos/${demo.id || demo._id}/confirm`, {
        method: 'PUT',
        body: JSON.stringify({ meeting_url: meetingUrl })
      });
      setActionMsg('Demo confirmed. Confirmation email sent.');
      setModal(null);
      fetchDemos();
    } catch {
      setActionMsg('Failed to confirm demo.');
    }
    setTimeout(() => setActionMsg(''), 3000);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Modals */}
      {modal?.type === 'cancel' && (
        <CancelModal demo={modal.demo} onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchDemos(); }} />
      )}
      {modal?.type === 'reschedule' && (
        <RescheduleModal demo={modal.demo} onClose={() => setModal(null)} onSaved={() => { setModal(null); fetchDemos(); }} />
      )}
      {modal?.type === 'confirm' && (
        <ConfirmDemoModal 
          demo={modal.demo} 
          onClose={() => setModal(null)} 
          onConfirm={handleConfirmAction} 
        />
      )}
      {modal?.type === 'details' && (
        <DetailsModal 
          demo={modal.demo} 
          onClose={() => setModal(null)} 
          onConfirm={(demo) => setModal({ type: 'confirm', demo })}
        />
      )}
      {showDrawer && (
        <ScheduleDemoDrawer onClose={() => setShowDrawer(false)} onSaved={() => { setShowDrawer(false); fetchDemos(); }} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#843FEF]" />
            Demo Scheduling
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage and schedule product demos</p>
        </div>
        <button
          onClick={() => setShowDrawer(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#843FEF] text-white rounded-xl text-sm font-semibold hover:bg-[#7230d4] shadow-md transition-colors"
        >
          <Plus className="w-4 h-4" />
          Schedule New Demo
        </button>
      </div>

      {actionMsg && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle className="w-4 h-4" /> {actionMsg}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" /> Filters:
          </div>
          <select
            value={filters.status}
            onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          >
            <option value="">All Statuses</option>
            <option value="All_Active">All Active</option>
            {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#843FEF]/30"
          />
          <button
            onClick={() => { setFilters({ status: '', date_from: '', date_to: '' }); setPage(1); }}
            className="text-sm text-[#843FEF] hover:underline ml-auto"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-[#843FEF] animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Contact', 'Organization', 'Date', 'Time', 'Platform', 'Assigned To', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {demos.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-400">No demos found.</td>
                  </tr>
                ) : demos.map((demo) => (
                  <tr key={demo.id || demo._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                      {demo.contact_person || demo.contact?.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {demo.organization_name || demo.organization || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(demo.demo_date)}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtTime(demo.demo_time)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Video className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-700 whitespace-nowrap">{demo.meeting_platform || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{demo.assigned_team_member || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge label={demo.status || '—'} colorClass={STATUS_COLORS[demo.status] || 'bg-gray-100 text-gray-600'} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {demo.meeting_url && (
                          <a
                            href={demo.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Join Meeting"
                            className="p-1.5 rounded-lg hover:bg-purple-50 text-gray-400 hover:text-[#843FEF] transition-colors"
                          >
                            <Video className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => handleComplete(demo)}
                          title="Mark Complete"
                          className="p-1.5 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        {(demo.status === 'Pending' || demo.notes?.includes('[PENDING REQUEST]')) && (
                          <button
                            onClick={() => setModal({ type: 'confirm', demo })}
                            title="Confirm Demo"
                            className="p-1.5 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => setModal({ type: 'cancel', demo })}
                          title="Cancel"
                          className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setModal({ type: 'reschedule', demo })}
                          title="Reschedule"
                          className="p-1.5 rounded-lg hover:bg-yellow-50 text-gray-400 hover:text-yellow-600 transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setModal({ type: 'details', demo })}
                          title="View Details"
                          className="p-1.5 rounded-lg hover:bg-purple-50 text-gray-400 hover:text-[#843FEF] transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMScheduleDemo;
